import { z } from "zod";
import { safeHttpsUrl } from "../../lib/safeUrl";
import type { PreviewCardProps } from "../../lib/previewCards";
import { clampText, MAX_PREVIEW_ROWS, PreviewEmpty, PreviewFooterLink, PreviewShowingOf } from "./shared";

/**
 * Shaped from observed `list_issues` structured output of the Linear MCP
 * server (mcp.linear.app/mcp): `{issues: [{id: "DEV-53", title, description,
 * priority: {value, name}, url, status, statusType, …}], hasNextPage,
 * cursor}`. `id` is the human identifier. Spine: an `issues` array of objects
 * with a string `title`; the rest is optional and `catch`-tolerant.
 */
export const linearIssuesSchema = z.object({
  issues: z.array(
    z.object({
      id: z.string().optional().catch(undefined),
      identifier: z.string().optional().catch(undefined),
      title: z.string(),
      url: z.string().optional().catch(undefined),
      status: z.string().optional().catch(undefined),
    }),
  ),
});

type LinearIssuesData = z.infer<typeof linearIssuesSchema>;

/** Read-only Linear issue list: identifier + title + status, linked when https. */
export function LinearIssuesCard({ data, connectorName }: PreviewCardProps<LinearIssuesData>) {
  const shown = data.issues.slice(0, MAX_PREVIEW_ROWS);

  return (
    <div className="flex flex-col">
      {shown.length === 0 ? (
        <PreviewEmpty />
      ) : (
        <ul className="flex flex-col">
          {shown.map((issue, index) => {
            const href = safeHttpsUrl(issue.url);
            const identifier = issue.identifier ?? issue.id;
            const body = (
              <>
                {identifier ? (
                  <span className="shrink-0 font-mono text-[12px] text-ink-muted">
                    {clampText(identifier, 24)}
                  </span>
                ) : null}
                <span className="min-w-0 flex-1 truncate font-sans text-[13px] text-ink">
                  {clampText(issue.title)}
                </span>
                {issue.status ? (
                  <span className="shrink-0 font-sans text-[12px] text-ink-soft">
                    {clampText(issue.status, 40)}
                  </span>
                ) : null}
              </>
            );
            return (
              <li key={index}>
                {href ? (
                  <a
                    href={href}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="flex items-center gap-2 px-3 py-2 transition-colors duration-150 ease-house hover:bg-canvas"
                  >
                    {body}
                  </a>
                ) : (
                  <div className="flex items-center gap-2 px-3 py-2">{body}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {data.issues.length > shown.length ? (
        <PreviewShowingOf shown={shown.length} total={data.issues.length} />
      ) : null}
      <PreviewFooterLink href="https://linear.app/" connector={connectorName || "Linear"} />
    </div>
  );
}
